<template>
  <div class="card">
    <div v-if="is_editing" class="card-header">
      {{ $t('competitions.title_edit') }}
    </div>
    <div v-else class="card-header">
      {{ $t('competitions.title_create') }}
    </div>
    <div class="card-body height_box">
      <h4>{{ $t('competitions.title_steps') }}</h4>
      <div class="row">
        <div class="col-12">
          <div class="card mb-3">
            <!-- Barra de navegación de pestañas -->
            <ul class="nav nav-tabs">
              <li
                  class="nav-item"
                  v-for="(tab, index) in tabs"
                  :key="index"
              >
                <a
                    class="nav-link"
                    :class="{ active: activeTab === index }"
                    href="#"
                    @click.prevent="changeTab(index)"
                >
                  <span class="fs-4 fw-bold text-dark">{{ tab.title }}</span>
                </a>
              </li>
            </ul>
            <!-- Contenido de cada tab -->
            <div class="tab-content mt-3">
              <div
                  class="tab-pane"
                  :class="{ active: activeTab === index }"
                  v-for="(tab, index) in tabs"
                  :key="index"
                  v-show="activeTab === index"
              >
                <form>
                  <div v-if="index === 0">
                    <div class="container">
                      <div class="row">
                        <!--Names-->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <label for="name" class="form-label">{{ $t('competitions.step_1.name') }}</label>
                            <input v-model="tab.fields.name" type="text" class="form-control" id="name" required>
                          </div>
                        </div>
                        <!--Types-->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <SelectType
                                v-model="tab.fields.type_id"
                                :options="types"
                                :label="$t('competitions.step_1.type')"
                                :placeholder="$t('competitions.step_1.select_type')"
                                id="type_id"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                            />
                          </div>
                        </div>
                        <!--Countries-->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <SelectType
                                v-model="tab.fields.country_id"
                                :options="countries"
                                :label="$t('competitions.step_1.country')"
                                :placeholder="$t('competitions.step_1.select_country')"
                                id="country_id"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                            />
                          </div>
                        </div>
                        <!--Rules-->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <SelectType
                                v-model="tab.fields.rule_id"
                                :options="rules"
                                :label="$t('competitions.step_1.rules')"
                                :placeholder="$t('competitions.step_1.select_rules')"
                                id="rule_id"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                                :iconButton="true"
                                :openInNewTab="true"
                                iconClass="fa-solid fa-circle-info"
                                :iconAction="'https://staging.slackline-tjs.com/rules'"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-12">
                          <div class="form-group mb-3 px-3">
                            <label for="date_range" class="form-label">{{ $t('competitions.step_1.date_range') }}</label>
                            <div v-if="tab.fields.type_id === 2" class="d-flex align-items-center my-2">
                              <input type="checkbox" v-model="isRange" @click="clearDataPicker" id="rangeToggle" class="me-2" />
                              <label for="rangeToggle">{{ $t('competitions.step_1.range_toggle') }}</label>
                            </div>
                            <VueDatePicker
                                v-model="date_range"
                                :range="isRange"
                                :multi-calendars="isRange"
                                :required="true"
                                :teleport="true"
                                :enableTimePicker="true"
                                :hour24="true"
                                :format="'dd/MM/yyyy HH:mm'"
                                :min-date="minDate"
                                :max-date="maxDate"
                                :start-date="minDate"
                                :flow="['calendar']"
                                id="date_range"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="index === 1">
                    <div class="container">
                      <!-- Buttons VS -->
                      <div class="row mb-2">
                        <div class="col-6 p-0">
                          <button
                              type="button"
                              :class="['btn-out', { active: tab.fields.is_vs === 1 }]"
                              @click="tab.fields.is_vs = 1"
                          >
                            {{ $t('competitions.step_2.vs') }}
                          </button>
                        </div>
                        <div class="col-6 p-0">
                          <button
                              type="button"
                              :class="['btn-out', { active: tab.fields.is_vs === 0 }]"
                              @click="tab.fields.is_vs = 0"
                          >
                            {{ $t('competitions.step_2.scoring') }}
                          </button>
                        </div>
                        <!-- Conditional text blocks -->
                        <div class="col-12 text-block-border" :class="{ 'bg-same-as-active': tab.fields.is_vs === 1 }" v-if="tab.fields.is_vs === 1">
                          <p>
                            <small>
                              {{ $t('competitions.step_2.vs_txt') }}
                            </small>
                          </p>
                        </div>
                        <div class="col-12 text-block-border" :class="{ 'bg-same-as-active': tab.fields.is_vs === 0 }" v-else-if="tab.fields.is_vs === 0">
                          <p>
                            <small>
                              {{ $t('competitions.step_2.scoring_txt') }}
                            </small>
                          </p>
                        </div>
                      </div>
                      <!-- Selects Gender and Category  -->
                      <div class="row mb-2">
                        <div class="col-md-6 px-1">
                          <div class="form-group mb-3 px-3">
                            <SelectType
                                v-model="tab.fields.gender"
                                :options="genders"
                                :label="$t('competitions.step_2.gender')"
                                :placeholder="$t('competitions.step_2.select_gender')"
                                id="gender"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                            />
                          </div>
                        </div>
                        <div class="col-md-6 px-1">
                          <div class="form-group mb-3 px-3">
                            <SelectType
                                v-model="tab.fields.category"
                                :options="categories"
                                :label="$t('competitions.step_2.category')"
                                :placeholder="$t('competitions.step_2.select_category')"
                                id="category"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                            />
                          </div>
                        </div>
                      </div>
                      <!-- Buttons Open -->
                      <div class="row mb-2">
                        <div class="col-6 p-0">
                          <button
                              type="button"
                              :class="['btn-out', { active: tab.fields.is_open === 1 }]"
                              @click="tab.fields.is_open = 1"
                          >
                            {{ $t('competitions.step_2.open') }}
                          </button>
                        </div>
                        <div class="col-6 p-0">
                          <button
                              type="button"
                              :class="['btn-out', { active: tab.fields.is_open === 0 }]"
                              @click="tab.fields.is_open = 0"
                          >
                            {{ $t('competitions.step_2.close') }}
                          </button>
                        </div>
                        <!-- Conditional text blocks -->
                        <div class="col-12 text-block-border" :class="{ 'bg-same-as-active': tab.fields.is_open === 1 }" v-if="tab.fields.is_open === 1">
                          <p>
                            <small>
                              {{ $t('competitions.step_2.open_text') }}
                            </small>
                          </p>
                        </div>
                        <div class="col-12 text-block-border" :class="{ 'bg-same-as-active': tab.fields.is_open === 0 }" v-else-if="tab.fields.is_open === 0">
                          <p>
                            <small>
                              {{ $t('competitions.step_2.close_text') }}
                            </small>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="index === 2">
                    <div class="container">
                      <div class="row mb-3">
                        <!-- Participants -->
                        <div class="col-6">
                          <div class="form-group mb-3">
                            <SelectType
                                v-model="tab.fields.participant_size"
                                :options="participants_pack"
                                :label="$t('competitions.step_3.select_participants')"
                                :placeholder="$t('competitions.step_3.select_participants')"
                                id="participant_size"
                                :required="true"
                                customClass="border-primary"
                                errorMessage="Este campo es obligatorio"
                                @change="onParticipantsChange"
                                :formatFunction="getMinMax"
                            />
                          </div>
                        </div>
                        <!-- Botones de Qualifications -->
                        <div class="col-6 d-flex flex-column align-items-center">
                          <label class="form-label text-center">{{ $t('competitions.step_3.qualification') }}</label>
                          <div class="d-flex justify-content-around w-100" style="max-width: 140px;">
                            <button
                                type="button"
                                :class="['btn btn-outline-primary', { active: tab.fields.is_qualification_dual === 0 }]"
                                @click="tab.fields.is_qualification_dual = 0"
                                :disabled="!tab.fields.participant_size"
                            >
                              <span class="fw-bold">1</span> <!-- Texto más grande y grueso -->
                            </button>
                            <!-- Contenedor que captura el clic incluso cuando el botón está deshabilitado -->
                            <div @click="handleClick" class="button-wrapper">
                              <button
                                  type="button"
                                  :class="['btn btn-outline-primary', { active: tab.fields.is_qualification_dual === 1 }]"
                                  :disabled="isDisabled"
                              >
                                <span class="fw-bold">2</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <!-- Stages -->
                        <div class="col-md-6">
                          <SelectType
                              v-model="selectedStage"
                              :options="availableStages"
                              :label="$t('competitions.step_3.stages')"
                              :placeholder="$t('competitions.step_3.select_stages')"
                              id="round_sizes"
                              :required="true"
                              :disabled="!tab.fields.participant_size"
                              customClass="border-primary"
                              errorMessage="Este campo es obligatorio"
                              @change="addStage"
                          />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label" for="round_sizes">{{ $t('competitions.step_3.selected_stages') }}</label>
                          <!-- Lista de seleccionados -->
                          <div class="selected-items">
                            <span v-for="stage in selectedStages" :key="stage.value" class="selected-item">
                              {{ stage.name }}
                              <button class="remove-btn" @click="removeStage(stage.value)">×</button>
                           </span>
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <!-- Judges -->
                        <div class="col-6">
                          <SelectType
                              v-model="tab.fields.judges"
                              :options="availableJudges"
                              :label="$t('competitions.step_3.judges')"
                              :placeholder="$t('competitions.step_3.select_judges')"
                              id="judges"
                              :required="true"
                              :disabled="!tab.fields.participant_size"
                              customClass="border-primary"
                              errorMessage="Este campo es obligatorio"
                          />
                        </div>
                        <!-- Days Online (Solo si se debe mostrar) -->
                        <div v-show="showBox" class="col-6">
                          <SelectType
                              v-model="tab.fields.days_online"
                              :options="availableDays"
                              :label="$t('competitions.step_3.days_online')"
                              :placeholder="$t('competitions.step_3.select_days_online')"
                              id="days_online"
                              :required="true"
                              :disabled="!tab.fields.participant_size"
                              customClass="border-primary"
                              errorMessage="Este campo es obligatorio"
                          />
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-12">
                          <!-- Contenedor principal con Flexbox -->
                          <div class="d-flex flex-wrap justify-content-center mt-3 info-container">
                            <!-- Fases -->
                            <div class="info-box">
                              <i class="fas fa-tasks"></i>
                              <span class="info-title">{{ $t('competitions.step_3.fases') }}</span>
                              <span class="info-label">Tot</span>
                              <span class="info-value">{{ computedStages }}</span>
                            </div>
                            <!-- Rondas -->
                            <div class="info-box">
                              <i class="fas fa-sync-alt"></i>
                              <span class="info-title">{{ $t('competitions.step_3.rounds') }}</span>
                              <div class="info-values-grid">
                                <div class="info-column">
                                  <span class="info-label">Min</span>
                                  <span class="info-value">{{ computedRounds.min }}</span>
                                </div>
                                <div class="info-column">
                                  <span class="info-label">Max</span>
                                  <span class="info-value">{{ computedRounds.max }}</span>
                                </div>
                              </div>
                            </div>
                            <!-- Tiempo de Grabación -->
                            <div class="info-box">
                              <i class="fas fa-video"></i>
                              <span class="info-title">{{ $t('competitions.step_3.rec_time') }}</span>
                              <div class="info-values-grid">
                                <div class="info-column">
                                  <span class="info-label">Min</span>
                                  <span class="info-value">{{ computedRecordingTime.min }}</span>
                                </div>
                                <div class="info-column">
                                  <span class="info-label">Max</span>
                                  <span class="info-value">{{ computedRecordingTime.max }}</span>
                                </div>
                              </div>
                            </div>
                            <!-- Tiempo de Evaluación -->
                            <div class="info-box">
                              <i class="fas fa-clipboard-check"></i>
                              <span class="info-title">{{ $t('competitions.step_3.judge_time') }}</span>
                              <div class="info-values-grid">
                                <div class="info-column">
                                  <span class="info-label">Min</span>
                                  <span class="info-value">{{ computedJudgingTime.min }}</span>
                                </div>
                                <div class="info-column">
                                  <span class="info-label">Max</span>
                                  <span class="info-value">{{ computedJudgingTime.max }}</span>
                                </div>
                              </div>
                            </div>
                            <!-- Tiempo Total del Concurso -->
                            <div  v-if="!computedDaysContest.status"  class="info-box">
                              <i class="fas fa-stopwatch"></i>
                              <span class="info-title">{{ $t('competitions.step_3.tot_time') }}</span>
                              <div class="info-values-grid">
                                <div class="info-column">
                                  <span class="info-label">Min</span>
                                  <span class="info-value">{{ computedContestTiming.min }}</span>
                                </div>
                                <div class="info-column">
                                  <span class="info-label">Max</span>
                                  <span class="info-value">{{ computedContestTiming.max }}</span>
                                </div>
                              </div>
                            </div>
                            <!-- Duración del Concurso en Días (Solo si type_id === 1) -->
                            <div v-if="computedDaysContest.status" class="info-box">
                              <i class="fas fa-calendar-alt"></i>
                              <span class="info-title">{{ $t('competitions.step_3.tot_days') }}</span>
                              <span class="info-value">{{ computedDaysContest.days }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else-if="index === 3">
                    <div class="container">
                      <div class="row align-items-end">
                        <!-- Fees (Monto) - Máximo 3 dígitos -->
                        <div class="col-md-4 col-5">
                          <div class="form-group mb-3 px-md-3">
                            <label for="fees" class="form-label">{{ $t('competitions.step_4.fees') }}</label>
                            <div class="input-group">
                              <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                              <input v-model="tab.fields.fees" type="number" class="form-control no-spin" id="fees" min="0" max="250000">
                            </div>
                          </div>
                        </div>
                        <!-- Total del premio + Botón alineado -->
                        <div class="col-md-6 col-7">
                          <div class="form-group mb-3 px-md-3 d-flex align-items-end">
                            <div class="w-100">
                              <label for="prize_amount_total" class="form-label">{{ $t('competitions.step_4.total_prize') }}</label>
                              <div class="input-group">
                                <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                                <input
                                    v-model="tab.fields.prize_amount_total"
                                    type="text"
                                    class="form-control no-spin"
                                    id="prize_amount_total"
                                    min="0"
                                    @input="formatTotalPrize"
                                    @blur="calculatePrizes"
                                />
                              </div>
                            </div>
                            <!-- Botón que cambia en móviles -->
                            <button class="btn btn-secondary btn-sm ms-2 d-none d-sm-block" @click="toggleFourthPrize">
                              {{ $t('competitions.step_4.fourth_place') }}?
                            </button>
                            <button class="btn btn-secondary btn-sm ms-2 d-sm-none" @click="toggleFourthPrize">
                              ?
                            </button>
                          </div>
                        </div>
                      </div>
                      <div class="row align-items-end">
                        <!-- Distribución de premios -->
                        <div class="col-md-3 col-6 compact-col">
                          <div class="form-group mb-3">
                            <label for="prize_1st" class="form-label">{{ $t('competitions.step_4.first_place') }}</label>
                            <div class="input-group">
                              <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                              <input v-model="tab.fields.prize_amount_first" type="number" class="form-control no-spin compact-label" id="prize_1st" readonly>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-3 col-6 compact-col">
                          <div class="form-group mb-3">
                            <label for="prize_2nd" class="form-label">{{ $t('competitions.step_4.second_place') }}</label>
                            <div class="input-group">
                              <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                              <input v-model="tab.fields.prize_amount_second" type="number" class="form-control no-spin compact-label" id="prize_2nd" readonly>
                            </div>
                          </div>
                        </div>

                        <div class="col-md-3 col-6 compact-col">
                          <div class="form-group mb-3">
                            <label for="prize_3rd" class="form-label">{{ $t('competitions.step_4.third_place') }}</label>
                            <div class="input-group">
                              <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                              <input v-model="tab.fields.prize_amount_third" type="number" class="form-control no-spin compact-label" id="prize_3rd" readonly>
                            </div>
                          </div>
                        </div>
                        <div v-if="showFourthPrize" class="col-md-3 col-6 compact-col">
                          <div class="form-group mb-3">
                            <label for="prize_4th" class="form-label">{{ $t('competitions.step_4.fourth_place') }}</label>
                            <div class="input-group">
                              <span class="input-group-text compact-text">{{ currencySymbol }}</span>
                              <input v-if="showFourthPrize" v-model="tab.fields.prize_amount_fourth" type="number" class="form-control no-spin compact-label" id="prize_4th" readonly>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Imagen para Competición -->
                      <div class="row">
                        <div class="col-12">
                          <div class="form-group mb-3 px-3 text-center">
                            <label class="form-label">{{ $t('competitions.step_4.img_path') }}</label>

                            <!-- VueCropperJS - Recorte Horizontal -->
                            <vue-cropper
                                v-if="showCropper"
                                ref="cropper"
                                :guides="false"
                                :src="image"
                                :autoCropArea="1"
                                :aspectRatio="16 / 9"
                                :viewMode="1"
                                :dragMode="dragMode"
                            ></vue-cropper>

                            <!-- Mostrar Imagen Recortada -->
                            <img
                                v-if="!showCropper"
                                :src="croppedImage || tab.fields.img_path || defaultImage"
                                class="img-fluid image-hover"
                                alt="Competition Image"
                                style="width: 100%; max-height: 200px; object-fit: cover; border: 1px solid #ccc; cursor: pointer;"
                                @click="openFileInput"
                            />
                            <input
                                v-if="!showCropper"
                                type="file"
                                ref="fileInput"
                                id="fileInput"
                                class="d-none"
                                @change="handleImageUpload"
                            />

                            <!-- Botones de Acción -->
                            <div v-if="showCropper" class="mt-2">
                              <button class="btn btn-warning btn-sm" @click.prevent="saveCroppedImage">
                                {{$t('competitions.btn_save')}}
                              </button>
                              <button class="btn btn-danger btn-sm ms-2" @click.prevent="cancelCropping">
                                {{$t('competitions.btn_close')}}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="row">
                        <!-- URL del evento -->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <label for="url_event" class="form-label">{{ $t('competitions.step_4.url_event') }}</label>
                            <input v-model="tab.fields.url_event" type="url" class="form-control" id="url_event" placeholder="https://example.com">
                          </div>
                        </div>

                        <!-- URL Redes Sociales -->
                        <div class="col-md-6">
                          <div class="form-group mb-3 px-3">
                            <label for="url_social" class="form-label">{{ $t('competitions.step_4.url_social') }}</label>
                            <input v-model="tab.fields.url_social" type="url" class="form-control" id="url_social" placeholder="https://facebook.com">
                          </div>
                        </div>
                      </div>
                      <!--Mostrar solo si es type_id = 2 -->
                      <template v-if="this.tabs[0].fields.type_id === 2">
                        <div class="row">
                          <!-- Ubicación (Google Maps URL) -->
                          <div class="col-md-12">
                            <div class="form-group mb-3 px-3">
                              <label for="location" class="form-label">{{ $t('competitions.step_4.location') }}</label>
                              <input v-model="tab.fields.location" type="url" class="form-control" id="location" placeholder="https://maps.google.com">
                            </div>
                          </div>
                        </div>
                        <!--          <div class="row">
                                    &lt;!&ndash; Frame Used &ndash;&gt;
                                    <div class="col-md-5">
                                      <div class="competitions-group mb-3">
                                        <label for="frame_used" class="competitions-label">{{ $t('competitions.step_4.frame_used') }}</label>
                                        <select v-model="tab.fields.frame_used" class="competitions-select" id="frame_used">
                                          <option disabled value="">{{ $t('competitions.step_4.select_matt_used') }}</option>
                                          <option v-for="(frame, index) in frameTypes" :key="index" :value="frame">{{ frame }}</option>
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="row">
                                    &lt;!&ndash; Tension System &ndash;&gt;
                                    <div class="col-md-6">
                                      <div class="competitions-group mb-3">
                                        <label for="tension_system" class="competitions-label">
                                          {{ $t('competitions.step_4.tension_system') }}
                                        </label>
                                        <select v-model="tab.fields.tension_system" class="competitions-select" id="tension_system">
                                          <option disabled value="">{{ $t('competitions.step_4.select_tension_system') }}</option>
                                          <option v-for="(system, index) in tensionSystems" :key="index" :value="system">
                                            {{ system }}
                                          </option>
                                        </select>
                                      </div>
                                    </div>
                                    &lt;!&ndash; Type of Anchor &ndash;&gt;
                                    <div class="col-md-6">
                                      <div class="competitions-group mb-3">
                                        <label for="type_anchor" class="competitions-label">{{ $t('competitions.step_4.type_anchor') }}</label>
                                        <select v-model="tab.fields.type_anchor" class="competitions-select" id="type_anchor">
                                          <option disabled value="">{{ $t('competitions.step_4.select_type_anchor') }}</option>
                                          <option v-for="(anchor, index) in anchorTypes" :key="index" :value="anchor">{{ anchor }}</option>
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="row">
                                    &lt;!&ndash; Longitud de la línea &ndash;&gt;
                                    <div class="col-md-3">
                                      <div class="competitions-group mb-3">
                                        <label for="length_line" class="competitions-label">{{ $t('competitions.step_4.length_line') }}</label>
                                        <input v-model="tab.fields.length_line" type="number" class="competitions-control" id="length_line" min="0">
                                      </div>
                                    </div>
                                    &lt;!&ndash; Altura desde el colchón &ndash;&gt;
                                    <div class="col-md-4">
                                      <div class="competitions-group mb-3">
                                        <label for="height_from_matt" class="competitions-label">{{ $t('competitions.step_4.height_from_matt') }}</label>
                                        <input v-model="tab.fields.height_from_matt" type="number" class="competitions-control" id="height_from_matt" min="0">
                                      </div>
                                    </div>
                                    &lt;!&ndash; Matt Used &ndash;&gt;
                                    <div class="col-md-5">
                                      <div class="competitions-group mb-3">
                                        <label for="matt_used" class="competitions-label">{{ $t('competitions.step_4.matt_used') }}</label>
                                        <select v-model="tab.fields.matt_used" class="competitions-select" id="matt_used">
                                          <option disabled value="">{{ $t('competitions.step_4.select_matt_used') }}</option>
                                          <option v-for="(matt, index) in mattTypes" :key="index" :value="matt">{{ matt }}</option>
                                        </select>
                                      </div>
                                    </div>
                                  </div>-->
                      </template>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer">
      <div class="row">
        <div class="col-12">
          <div class="mb-3">
            <!-- Barra de progreso de ejemplo -->
            <div class="progress mt-2">
              <div
                  class="progress-bar"
                  role="progressbar"
                  :style="{ width: completionPercentage + '%' }"
                  aria-valuenow="completionPercentage"
                  aria-valuemin="0"
                  aria-valuemax="100"
              >
                {{ completionPercentage }}%
              </div>
            </div>
          </div>
          <!-- Botones de Guardar y Cancelar -->
          <div class="d-flex gap-2 my-3 justify-content-center">
<!--            <button type="button" class="btn btn-warning" :hidden="!isPublishEnabled">
              <i class="fa-regular fa-upload text-white"></i>
              {{ $t('competitions.btn_publish') }}
            </button>-->
            <button type="button" @click="saveForm" class="btn btn-primary">
              <i class="fa-regular fa-save text-white"></i>
              {{ $t('competitions.btn_save') }}
            </button>
            <button
                type="button"
                class="btn btn-secondary"
                @click="cancelForm()"
            >
              <i class="fa-regular fa-x text-white"></i>
              {{ $t('competitions.btn_cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import 'cropperjs/dist/cropper.css';
import {mapActions, mapState} from "vuex";
import SelectType from "@/components/competitions/SelectType.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  name: "CompetitionsForm",
  components: {
    VueDatePicker,
    SelectType,
    VueCropper
  },
  props: {
    /*Step1*/
    types: {
      type: Object,
      required: true
    },
    countries: {
      type: Array,
      required: true
    },
    rules: {
      type: Array,
      required: true
    },
    tricklistVersions: {
      type: Array,
      required: false,
      default: () => []
    },
    /*Step2*/
    genders: {
      type: Array,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    /*Step3*/
    participants_pack: {
      type: Array,
      required: true
    },
    round_sizes: {
      type: Array,
      required: true
    },
    availableJudges: {
      type: Array,
      required: true
    },
    availableDays: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      tabs: [],
      activeTab: 0,
      is_editing: false,
      isRange: false,
      date_range: null,
      selectedStage: "",
      selectedStages: [],
      showFourthPrize: false,
      showCropper: false,
      croppedImage: null,
      image: null,
      dragMode: "move",
      finalImagePath: null,
      defaultImage: require("@/assets/competition.jpg"),
    };
  },
  watch: {
    tabs: {
      handler(newTabs) {
        this.setCompetition(newTabs);
      },
      deep: true
    },
    // Este watch ya existe, pero ahora maneja también la lógica de isRange
    date_range: {
      handler(newValue) {
        if (Array.isArray(newValue)) {
          this.tabs[0].fields.date_start = newValue[0] ? this.formatDate(newValue[0]) : null;
          this.tabs[0].fields.date_end = newValue[1] ? this.formatDate(newValue[1]) : null;
          this.isRange = !!(newValue[0] && newValue[1] && newValue[0] !== newValue[1]);
        } else {
          this.tabs[0].fields.date_start = newValue ? this.formatDate(newValue) : null;
          this.tabs[0].fields.date_end = newValue ? this.formatDate(newValue) : null;
          this.isRange = false;
        }
      },
      deep: true
    },
    round_sizes: {
      handler(newValue) {
        if (
            newValue.length > 0 &&
            this.tabs &&
            this.tabs.length > 2 &&
            this.tabs[2].fields &&
            this.tabs[2].fields.round_size_id.length > 0
        ) {
          this.updateStages(this.tabs[2].fields.round_size_id);
        } else {
          console.warn("⚠️ `this.tabs[2]` aún no está definido o no tiene los datos esperados.");
        }
      },
      deep: true,
      immediate: true
    },
    // Nuevo watch para actualizar `date_range` cuando los datos llegan de la base de datos
    "tabs[0].fields.date_start": function(newStart) {
      this.updateDateRange(newStart, this.tabs[0].fields.date_end);
    },
    "tabs[0].fields.date_end": function(newEnd) {
      this.updateDateRange(this.tabs[0].fields.date_start, newEnd);
    },
    tricklistVersions: {
      handler(newValue) {
        this.applyDefaultTricklistVersion(newValue);
      },
      immediate: true
    },
  },
  computed: {
    ...mapState("competition",["competitionIdToEdit", "competitionData"]),
    ...mapState('user', ['isAuthenticated','user','token']),
    completionPercentage() {
      if (!this.tabs || this.tabs.length === 0 || !this.tabs[0]) {
        return 0; // ⚠️ Retornar 0 si los datos aún no están listos
      }

      // Campos obligatorios generales
      let requiredFields = [
        { tabIndex: 0, field: 'name' },
        { tabIndex: 0, field: 'date_start' },
        { tabIndex: 0, field: 'date_end' },
        { tabIndex: 0, field: 'type_id' },
        { tabIndex: 0, field: 'country_id' },
        { tabIndex: 0, field: 'rule_id' },
        { tabIndex: 0, field: 'tricklist_version_id' },
        { tabIndex: 1, field: 'is_vs' },
        { tabIndex: 1, field: 'gender' },
        { tabIndex: 1, field: 'category' },
        { tabIndex: 1, field: 'is_open' },
        { tabIndex: 2, field: 'participant_size' },
        { tabIndex: 2, field: 'is_qualification_dual' },
        { tabIndex: 2, field: 'judges' },
        { tabIndex: 3, field: 'fees' },
        { tabIndex: 3, field: 'prize_amount_total' }, // 🔹 Campo anidado
        { tabIndex: 3, field: 'img_path' },
        { tabIndex: 3, field: 'url_event' },
        { tabIndex: 3, field: 'url_social' },
      ];

      // 📌 Definir campos adicionales según `type_id`
      const extraFieldsByType = {
        1: [{ tabIndex: 2, field: 'days_online' }],
        2: [
          { tabIndex: 3, field: 'location' },
          /* { tabIndex: 3, field: 'length_line' },
           { tabIndex: 3, field: 'type_anchor' },
           { tabIndex: 3, field: 'tension_system' },
           { tabIndex: 3, field: 'height_from_matt' },
           { tabIndex: 3, field: 'matt_used' },
           { tabIndex: 3, field: 'frame_used' },*/
        ],
        3: [],
      };

      // 🔄 Si `type_id` tiene campos extra, los añadimos
      const typeId = this.tabs[0]?.fields?.type_id || null;
      if (typeId && extraFieldsByType[typeId]) {
        requiredFields = [...requiredFields, ...extraFieldsByType[typeId]];
      }

      let filledCount = 0;
      let empty = [];

      requiredFields.forEach(({ tabIndex, field }) => {
        if (this.tabs[tabIndex]) {
          let value = null;
          if (Object.hasOwn(this.tabs[tabIndex].fields, field)) {
            value = this.tabs[tabIndex].fields[field];
          }
          if (fieldValueIsFilled(value)){
            filledCount++;
          }else{
            empty.push(field);
          }
        } else {
          console.warn(`Tab ${tabIndex} or field "${field}" not found`);
        }
      });

      return requiredFields.length > 0 ? Math.round((filledCount / requiredFields.length) * 100) : 0;
      // 🎯 Función auxiliar para validar si un valor está relleno
      function fieldValueIsFilled(val) {
        return (
            val !== null &&
            val !== '' &&
            val !== false &&
            !(Array.isArray(val) && val.length === 0)
        );
      }
    },
    // isPublishEnabled() {
    //   return this.completionPercentage === 100;
    // },
    isDisabled() {
      return this.tabs[2].fields.participant_size === 'big' && this.tabs[0].fields.type_id === 2 || !this.tabs[2].fields.participant_size;
    },
    availableStages() {
      if (!this.round_sizes.length || !this.tabs[2].fields.participant_size) return [];

      const participantsGroup = this.participants_pack.find(
          pack => pack.value === this.tabs[2].fields.participant_size
      );
      if (!participantsGroup) return [];

      const participants = this.tabs[1].fields.is_vs === 1 ? participantsGroup.max_vs : participantsGroup.max_sc;

      let maxStage = 1;

      if (participants >= 32) {
        maxStage = 5;
      } else if (participants >= 16) {
        maxStage = 4;
      } else if (participants >= 8) {
        maxStage = 3;
      } else if (participants >= 4) {
        maxStage = 2;
      }

      // Filtrar stages disponibles:
      return this.round_sizes.filter(stage =>
          stage.value <= maxStage && // No sobrepasar el máximo permitido
          !this.selectedStages.some(selected => selected.value === stage.value) && // No repetir stages
          !(this.tabs[1].fields.is_vs === 0 && stage.value === 2) // ⚠️ Bloquea "Semifinal" si `is_vs === 0`
      );
    },
    showBox() {
      return this.tabs[0].fields.type_id === 1;
    },
    computedStages() {
      return this.calculateStages();
    },
    computedRounds() {
      return this.calculateRounds();
    },
    computedRecordingTime() {
      return this.calculateRecordingTime();
    },
    computedJudgingTime() {
      return this.calculateJudgingTime();
    },
    computedContestTiming() {
      return this.calculateContestTiming();
    },
    computedDaysContest() {
      return this.calculateDays();
    },
    currencySymbol() {
      const country = this.countries.find(c => c.id === this.tabs[0].fields.country_id);
      return country ? country.currency_symbol : "€";
    },
    minDate() {
      let min = new Date();
      min.setMonth(min.getMonth() + 1); // Un mes adelante
      return min;
    },
    maxDate() {
      let max = new Date(this.minDate); // Basado en minDate
      max.setFullYear(max.getFullYear() + 1); // Un año desde minDate
      return max;
    }
  },
  async mounted() {
    this.startLoading();
    const competitionIdToEdit = this.competitionIdToEdit;
    let competitionData = this.competitionData;

    if (competitionIdToEdit) {
      // 🔹 Modo Edición o Recarga
      if (!competitionData || competitionData.length === 0) {
        await this.fetchUserCompetition(competitionIdToEdit);
        competitionData = this.competitionData; // Volvemos a obtener los datos de sesión
      }

      if (competitionData && competitionData.length) {
        this.tabs = competitionData;
        this.is_editing = true;

        this.$nextTick(() => {
          this.updateDateRange(
              this.tabs[0].fields.date_start,
              this.tabs[0].fields.date_end
          );
          this.updateStages(
              this.tabs[2].fields.round_size_id,
          );
        });

      } else {
        this.createTabs();
      }

    } else {
      // 🔹 Modo Creación (No hay ID de edición)
      if (competitionData && competitionData.length) {
        this.tabs = competitionData;
        this.$nextTick(() => {
          this.updateDateRange(
              this.tabs[0].fields.date_start,
              this.tabs[0].fields.date_end
          );
        });
      } else {
        this.createTabs();
      }
    }

    this.stopLoading();
  },
  methods: {
    ...mapActions("competition",["setCompetition"]),
    /* Métodos para Tabs */
    createTabs() {
      this.tabs = [
        {
          title: "1",
          fields: {
            name: "",
            date_start: null,
            date_end: null,
            type_id: "",
            rule_id: "",
            tricklist_version_id: "",
            country_id: "",
          }
        },
        {
          title: "2",
          fields: {
            is_vs: null,
            gender: "",
            category: "",
            is_open: null,
          }
        },
        {
          title: "3",
          fields: {
            is_qualification_dual: null,
            participant_size: "",
            round_size_id: [],
            judges: "",
            days_online: "",
          }
        },
        {
          title: "4",
          fields: {
            fees: "",
            location: "",
            prize_amount_total: "",
            prize_amount_first: "",
            prize_amount_second: "",
            prize_amount_third: "",
            prize_amount_fourth: "",
            img_path: null,
            url_event: "",
            url_social: "",
            /* length_line: "",
             type_anchor: "",
             tension_system: "",
             height_from_matt: "",
             matt_used: "",
             frame_used: ""*/
          }
        }
      ];
      this.applyDefaultTricklistVersion(this.tricklistVersions);
      this.setCompetition(this.tabs);
      this.is_editing = false;
    },
    changeTab(index) {
      // ⚠️ Condition: Prevent advancing if `type_id` is missing in Step 1
            if (index > 0 && !this.tabs[0].fields.type_id) {
              this.$emit("update-show-modal", this.$t('competitions.messages.select_competition_type'));
              return; // Block tab change
            }

      // ⚠️ Condition: Prevent advancing if `is_vs` is not selected in Step 2
            if (index > 1 && this.tabs[1].fields.is_vs === null) {
              this.$emit("update-show-modal", this.$t('competitions.messages.select_vs_or_scoring'));
              return; // Block tab change
            }

      // ⚠️ Condition: Prevent advancing if `is_open` is not selected in Step 2
            if (index > 1 && this.tabs[1].fields.is_open === null) {
              this.$emit("update-show-modal", this.$t('competitions.messages.select_open_or_close'));
              return; // Block tab change
            }

      // If everything is fine, change tab
      this.activeTab = index;
    },
    populateTabs(data) {
      this.tabs = [
        {
          title: "1",
          fields: {
            name: data.name || "",
            date_start: data.competitions_info?.date_start || null,
            date_end: data.competitions_info?.date_end || null,
            type_id: data.type_id || "",
            rule_id: data.rule_id || "",
            tricklist_version_id: data.tricklist_version_id || "",
            country_id: data.competitions_info?.country_id || "",
          }
        },
        {
          title: "2",
          fields: {
            is_vs: data.is_vs ?? null,
            gender: data.competitions_info?.gender || "",
            category: data.competitions_info?.category || "",
            is_open: data.is_open ?? null,
          }
        },
        {
          title: "3",
          fields: {
            is_qualification_dual: data.is_qualification_dual !== null ? Number(data.is_qualification_dual) : null,
            participant_size: data.participant_size || "",
            judges: data.judges || "",
            days_online: data.days_online || "",
            round_size_id: Array.isArray(data.round_sizes)
                ? data.round_sizes.map(stage => stage.id)
                : [],
          }
        },
        {
          title: "4",
          fields: {
            fees: data.competitions_info?.fees || "",
            location: data.competitions_info?.location || "",
            prize_amount_total: data.competitions_info?.prize_amount || "",
            img_path: data.competitions_info?.img_path || "",
            url_event: data.competitions_info?.url_event || "",
            url_social: data.competitions_info?.url_social || "",
          }
        }
      ];

      this.applyDefaultTricklistVersion(this.tricklistVersions);
      this.setCompetition(this.tabs); // Guardamos en sesión

      this.$nextTick(() => {
        this.updateDateRange(
            this.tabs[0].fields.date_start,
            this.tabs[0].fields.date_end
        );
        this.updateStages(
            this.tabs[2].fields.round_size_id,
        );
        this.formatTotalPrize();
        this.calculatePrizes();
      });

      this.is_editing = true;
    },
    async fetchUserCompetition(id) {
      try {
        const response = await this.$axios.get(`/competition/${id}`, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.status === 200 && response.data?.competition) {
          this.populateTabs(response.data.competition);
        } else {
          console.warn("⚠️ PASO 6: Respuesta inesperada de la API:", response);
        }
      } catch (error) {
        console.error("❌ PASO 6: Error al obtener la competencia:", error);
      }
    },
    applyDefaultTricklistVersion(options = this.tricklistVersions) {
      const defaultVersionId = Array.isArray(options) && options.length ? options[0].value : null;

      if (!defaultVersionId || !Array.isArray(this.tabs) || !this.tabs[0]?.fields) {
        return;
      }

      if (!this.tabs[0].fields.tricklist_version_id) {
        this.tabs[0].fields.tricklist_version_id = defaultVersionId;
      }
    },
    /*Control competitions*/
    saveForm() {
      this.$emit("save-form");
    },
    cancelForm() {
      this.$emit("cancel-form");
    },
    startLoading() {
      this.$emit("show-loading");
    },
    stopLoading() {
      this.$emit("hide-loading");
    },
    /*Step1*/
    clearDataPicker() {
      this.$nextTick(() => {
        this.date_range = null;
        this.tabs[0].fields.date_start = null;
        this.tabs[0].fields.date_end = null;
      });
    },
    formatDate(date) {
      if (!date) return null; // Manejar casos donde la fecha sea nula o indefinida
      const d = new Date(date);
      const year = String(d.getFullYear()); // Últimos 2 dígitos del año
      const month = String(d.getMonth() + 1).padStart(2, '0'); // Mes (1-12)
      const day = String(d.getDate()).padStart(2, '0'); // Día (1-31)
      const hours = String(d.getHours()).padStart(2, '0'); // Horas (0-23)
      const minutes = String(d.getMinutes()).padStart(2, '0'); // Minutos (0-59)
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    updateDateRange(start, end) {
      if (start && end) {
        const parsedStart = new Date(start);
        const parsedEnd = new Date(end);

        // Si las fechas son distintas, activar rango y asignar array
        if (parsedStart.getTime() !== parsedEnd.getTime()) {
          this.isRange = true;
          this.date_range = [parsedStart, parsedEnd];
        } else {
          this.isRange = false;
          this.date_range = parsedStart;
        }
      } else {
        this.date_range = null; // Resetear si los valores son nulos
      }
    },
    /*Step3*/
    initializeStages() {
 /*     const finalStage = this.round_sizes.find(stage => stage.value === 1);
      if (finalStage && !this.selectedStages.some(stage => stage.value === 1)) {
        this.selectedStages.push(finalStage);
      }*/
      if (!Array.isArray(this.tabs[2].fields.round_size_id)) {
        this.tabs[2].fields.round_size_id = [];
      }
   /*   if (!this.tabs[2].fields.round_size_id.includes(1)) {
        this.tabs[2].fields.round_size_id.push(1);
      }*/
      this.tabs[2].fields.is_qualification_dual = 0;
      this.tabs[2].fields.judges = 1;
      if (this.tabs[0].fields.type_id === 1){
        this.tabs[2].fields.days_online = 10;
      }else{
        this.tabs[2].fields.days_online = "";
      }
    },
    onParticipantsChange() {
      this.selectedStages = [];
      this.selectedStage = "";
      if (this.tabs[2].fields?.round_size_id !== undefined) {
        this.tabs[2].fields.round_size_id = [];
      }
      this.initializeStages();
    },
    getMinMax(participant) {
      return this.tabs[1].fields.is_vs === 1
          ? `${participant.min_vs} - ${participant.max_vs}`
          : `${participant.min_sc} - ${participant.max_sc}`;
    },
    handleClick(event) {
      if (this.isDisabled) {
        if (!this.tabs[2].fields.participant_size) {
          this.$emit("update-show-modal", this.$t('competitions.messages.participant_size'));
        } else if (this.tabs[2].fields.participant_size === 'big' && this.tabs[0].fields.type_id === 2) {
          this.$emit("update-show-modal", this.$t('competitions.messages.double_parameters'));
        }
      } else {
        // Si no está deshabilitado, permite la acción normal
        this.tabs[2].fields.is_qualification_dual = 1;
      }
      event.preventDefault();
    },
    addStage() {
      if (this.selectedStage === null) return; // Evitar agregar Final nuevamente

      const stageValue = Number(this.selectedStage);
      const stage = this.round_sizes.find(s => s.value === stageValue);

      if (stage && !this.selectedStages.some(s => s.value === stage.value)) {
        this.selectedStages.push(stage);

        if (!Array.isArray(this.tabs[2].fields.round_size_id)) {
          this.tabs[2].fields.round_size_id = [];
        }

        this.tabs[2].fields.round_size_id.push(stage.value);

        // Forzar actualización del selector limpiando `selectedStage`
        this.selectedStage = null;
        this.$nextTick(() => { this.selectedStage = ""; });
      }
    },
    removeStage(stageValue) {
      // if (stageValue === 1) return;

      this.selectedStages = this.selectedStages.filter(stage => stage.value !== stageValue);
      this.tabs[2].fields.round_size_id = this.tabs[2].fields.round_size_id.filter(id => id !== stageValue);
    },
    calculateStages() {
      let countStages = this.tabs[2].fields.round_size_id.length || 0;
      let countQualification = this.tabs[2].fields.is_qualification_dual === null ? 0
          : this.tabs[2].fields.is_qualification_dual === 0 ? 1
              : 2;
      return countStages + countQualification || 0;
    },
    calculateRounds() {
      // Verificar si hay `participants_size` seleccionado
      if (!this.tabs[2].fields.participant_size) return { min: 0, max: 0 };

      // Obtener el pack de participantes correspondiente (small, medium, big)
      const participantsPack = this.participants_pack.find(
          pack => pack.value === this.tabs[2].fields.participant_size
      );

      if (!participantsPack) return { min: 0, max: 0 };

      // Determinar valores de min y max según si es VS o SC
      const isVS = this.tabs[1].fields.is_vs;
      const minParticipants = isVS ? participantsPack.min_vs : participantsPack.min_sc;
      const maxParticipants = isVS ? participantsPack.max_vs : participantsPack.max_sc;
      const qualifications = this.tabs[2].fields.is_qualification_dual ? 2 : 1;
      // ⚠️ Base: cada participante juega al menos 1 ronda
      let minRounds = minParticipants * qualifications;
      let maxRounds = maxParticipants * qualifications;

      // ⚠️ Sumar rondas extra según los valores de `round_size_id`
      let round_size_ids = this.tabs[2].fields.round_size_id || [];
      round_size_ids.forEach(stage => {
        switch (stage) {
          case 1: minRounds += 4; maxRounds += 4; break;  // Final
          case 2: minRounds += 4; maxRounds += 4; break;  // Semifinal
          case 3: minRounds += 8; maxRounds += 8; break;  // Cuartos
          case 4: minRounds += 16; maxRounds += 16; break; // Octavos
          case 5: minRounds += 32; maxRounds += 32; break; // Dieciseisavos
        }
      });

      return { min: minRounds, max: maxRounds };
    },
    calculateRecordingTime() {
      if (!this.tabs[2].fields.participant_size) return { min: "00:00", max: "00:00" };

      // Obtener el paquete de participantes según el tamaño seleccionado
      const participantsPack = this.participants_pack.find(
          pack => pack.value === this.tabs[2].fields.participant_size
      );
      if (!participantsPack) return { min: "00:00", max: "00:00" };

      // Determinar valores de min y max según si es VS o SC
      const isVS = this.tabs[1].fields.is_vs;
      const minParticipants = isVS ? participantsPack.min_vs : participantsPack.min_sc;
      const maxParticipants = isVS ? participantsPack.max_vs : participantsPack.max_sc;
      // Tabla de tiempos base por stage según type_id e is_vs
      const timeTable = {
        0:{
          // Type 1
          1: { qualification: 1.5, normal: 2, final: 3 }, // No VS
          // Type 2
          2: { qualification: 2, normal: 4, final: 5 }, // No VS
          // Type 3
          3: { qualification: 1, normal: 2, final: 3 }, // No VS
        },
        1:{
          // Type 1
          1: { qualification: 1.5, normal: 2, final: 3 }, // VS
          // Type 2
          2: { qualification: 2, normal: 10, final: 10 }, // VS
          // Type 3
          3: { qualification: 1, normal: 2, final: 3 }  // VS
        },
      };

      // Determinar `type_id` y si es VS
      const typeBase = this.tabs[0].fields.type_id;

      // Seleccionar la configuración de tiempos correcta, con fallback a Type 2 (id 2)
      const timeSettings = timeTable[isVS][typeBase] || timeTable[0][1];
      // Base: cada participante tiene un tiempo de `qualification` minutos
      const isQ = this.tabs[2].fields.is_qualification_dual ? 2 : 1;
      let minTime = (minParticipants * timeSettings.qualification) * isQ;
      let maxTime = (maxParticipants * timeSettings.qualification) * isQ;

      // Agregar tiempo extra por fases seleccionadas
      let round_size_ids = this.tabs[2].fields.round_size_id || [];
      round_size_ids.forEach(stage => {
        switch (stage) {
          case 1: minTime += (timeSettings.final * 4); maxTime += (timeSettings.final * 4); break;  // Final
          case 2: minTime += (timeSettings.normal * 4); maxTime += (timeSettings.normal * 4); break;  // Semifinal
          case 3: minTime += (timeSettings.normal * 8); maxTime += (timeSettings.normal * 8); break;  // Cuartos
          case 4: minTime += (timeSettings.normal * 16); maxTime += (timeSettings.normal * 16); break; // Octavos
          case 5: minTime += (timeSettings.normal * 32); maxTime += (timeSettings.normal * 32); break; // Dieciseisavos
        }
      });
      // Convertimos el tiempo total en formato HH:mm
      return {
        min: this.convertMinutesToHHMM(minTime),
        max: this.convertMinutesToHHMM(maxTime)
      };
    },
    calculateJudgingTime() {
      if (!this.tabs[2].fields.participant_size) return { min: "00:00", max: "00:00" };

      // Obtener el paquete de participantes según el tamaño seleccionado
      const participantsPack = this.participants_pack.find(
          pack => pack.value === this.tabs[2].fields.participant_size
      );
      if (!participantsPack) return { min: "00:00", max: "00:00" };

      // Determinar valores de min y max según si es VS o SC
      const isVS = this.tabs[1].fields.is_vs;
      const minParticipants = isVS ? participantsPack.min_vs : participantsPack.min_sc;
      const maxParticipants = isVS ? participantsPack.max_vs : participantsPack.max_sc;
      // Tabla de tiempos base por stage según type_id e is_vs
      const timeTable = {
        0:{
          // Type 1
          1: { qualification: 10, normal: 10, final: 12 }, // No VS
          // Type 2
          2: { qualification: 7, normal: 15, final: 10 }, // No VS
          // Type 3
          3: { qualification: 1, normal: 2, final: 3 }, // No VS
        },
        1:{
          // Type 1
          1: { qualification: 10, normal: 10, final: 12 }, // VS
          // Type 2
          2: { qualification: 7, normal: 15, final: 10 }, // VS
          // Type 3
          3: { qualification: 1, normal: 2, final: 3 }  // VS
        },
      };

      // Determinar `type_id` y si es VS
      const typeBase = this.tabs[0].fields.type_id;

      // Seleccionar la configuración de tiempos correcta, con fallback a Type 2 (id 2)
      const timeSettings = timeTable[isVS][typeBase] || timeTable[0][1];

      // Base: cada participante tiene un tiempo de `qualification` minutos
      const isQ = this.tabs[2].fields.is_qualification_dual ? 2 : 1;
      let minTime = (minParticipants * timeSettings.qualification) * isQ;
      let maxTime = (maxParticipants * timeSettings.qualification) * isQ;

      // Agregar tiempo extra por fases seleccionadas
      let round_size_ids = this.tabs[2].fields.round_size_id || [];
      round_size_ids.forEach(stage => {
        switch (stage) {
          case 1: minTime += (timeSettings.final * 4); maxTime += (timeSettings.final * 4); break;  // Final
          case 2: minTime += (timeSettings.normal * 4); maxTime += (timeSettings.normal * 4); break;  // Semifinal
          case 3: minTime += (timeSettings.normal * 8); maxTime += (timeSettings.normal * 8); break;  // Cuartos
          case 4: minTime += (timeSettings.normal * 16); maxTime += (timeSettings.normal * 16); break; // Octavos
          case 5: minTime += (timeSettings.normal * 32); maxTime += (timeSettings.normal * 32); break; // Dieciseisavos
        }
      });
      // Convertimos el tiempo total en formato HH:mm
      return {
        min: this.convertMinutesToHHMM(minTime), // Estimado multiplicado por 2
        max: this.convertMinutesToHHMM(maxTime)
      };
    },
    calculateContestTiming() {
      const recTime = this.calculateRecordingTime();
      const judgeTime = this.calculateJudgingTime();

      let judges = this.tabs[2].fields.judges || 1;

      // ✅ Convertir "HH:mm" a minutos antes de operar
      const recMin = this.convertHHMMToMinutes(recTime.min);
      const recMax = this.convertHHMMToMinutes(recTime.max);
      const judgeMin = this.convertHHMMToMinutes(judgeTime.min);
      const judgeMax = this.convertHHMMToMinutes(judgeTime.max);

      // ✅ Hacer la suma y dividir por jueces
      let totTimeMin = recMin + (judgeMin / judges);
      let totTimeMax = recMax + (judgeMax / judges);

      // ✅ Convertir de vuelta a formato HH:mm
      return {
        min: this.convertMinutesToHHMM(totTimeMin),
        max: this.convertMinutesToHHMM(totTimeMax)
      };
    },
    calculateDays(){
      let statusCalc = this.tabs[0].fields.type_id === 1 ? 1 : 0;
      let daysCalc = this.tabs[2].fields.days_online * this.calculateStages();
      return {
        status: statusCalc,
        days: daysCalc,
      };
    },
    convertMinutesToHHMM(minutes) {
      let hours = Math.floor(minutes / 60);
      let mins = Math.round(minutes % 60);
      return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    },
    convertHHMMToMinutes(timeStr) {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours * 60 + minutes;
    },
    updateStages(stagesArray) {
      if (!stagesArray || stagesArray.length === 0) {
        console.log("⚠️ updateStages: No hay stages para actualizar.");
        return;
      }

      this.$nextTick(() => {
        this.selectedStages = stagesArray.map(stageId => {
          const stage = this.round_sizes.find(stage => stage.value === stageId);
          return stage ? stage : null;
        }).filter(stage => stage !== null);
      });
    },
    /*Step4*/
    formatTotalPrize() {
      this.tabs[3].fields.prize_amount_total = this.formatNumber(
          this.unformatNumber(this.tabs[3].fields.prize_amount_total)
      );
    },
    calculatePrizes() {
      let total = this.unformatNumber(this.tabs[3].fields.prize_amount_total) || 0;

      if (this.showFourthPrize) {
        // Distribución con 4 premios
        this.tabs[3].fields.prize_amount_first = this.formatNumber(Math.round(total * 0.4));
        this.tabs[3].fields.prize_amount_second = this.formatNumber(Math.round(total * 0.3));
        this.tabs[3].fields.prize_amount_third = this.formatNumber(Math.round(total * 0.2));
        this.tabs[3].fields.prize_amount_fourth = this.formatNumber(Math.round(total * 0.1));
      } else {
        // Distribución con 3 premios
        this.tabs[3].fields.prize_amount_first = this.formatNumber(Math.round(total * 0.5));
        this.tabs[3].fields.prize_amount_second = this.formatNumber(Math.round(total * 0.3));
        this.tabs[3].fields.prize_amount_third = this.formatNumber(Math.round(total * 0.2));
        this.tabs[3].fields.prize_amount_fourth = "0"; // Se borra si no se usa
      }
    },
    formatNumber(value) {
      if (!value) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    unformatNumber(value) {
      if (!value) return 0;
      return parseInt(value.toString().replace(/\./g, ""), 10);
    },
    toggleFourthPrize(event) {
      this.showFourthPrize = !this.showFourthPrize;
      event.preventDefault();
      this.calculatePrizes(); // Recalcular premios al activar/desactivar el 4º puesto
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.image = e.target.result;
          this.showCropper = true;
        };
        reader.readAsDataURL(file);
      }
    },
    cancelCropping() {
      this.showCropper = false;
    },
    openFileInput() {
      this.$nextTick(() => {
        const fileInput = document.querySelector("#fileInput");
        if (fileInput) {
          fileInput.click();
        } else {
          console.error("No se encontró el input de archivo en el DOM.");
        }
      });
    },
    saveCroppedImage() {
      this.$nextTick(() => {
        let cropperInstance = Array.isArray(this.$refs.cropper) ? this.$refs.cropper[0] : this.$refs.cropper;

        if (cropperInstance && cropperInstance.getCroppedCanvas) {
          const canvas = cropperInstance.getCroppedCanvas({
            width: 1280,
            height: 720,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high'
          });

          canvas.toBlob(blob => {
            if (!blob) {
              console.error("❌ No se generó el Blob correctamente.");
              return;
            }

            // 🔥 Convertir el Blob en un File con nombre
            const file = new File([blob], "image.jpg", { type: blob.type });

            // 🔍 Verificar el archivo antes de enviarlo

            this.croppedImageBlob = file; // Guardamos el archivo para enviarlo
            this.croppedImage = URL.createObjectURL(file); // Previsualización
            this.showCropper = false;
            this.uploadImageToServer();
          }, 'image/jpeg', 0.7);
        } else {
          console.error("❌ VueCropper no soporta getCroppedCanvas.");
        }
      });
    },
    async uploadImageToServer() {
      if (!this.croppedImageBlob) {
        console.error("❌ No hay imagen para subir.");
        return;
      }

      const formData = new FormData();
      formData.append("image", this.croppedImageBlob, "image.jpg");
      formData.append("store", "competitions_banners");

      this.startLoading();
      try {
        const response = await this.$axios.post(`/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "multipart/competitions-data",
          },
        });

        if (response.status === 200) {
          this.finalImagePath = response.data.path; // Guardamos la imagen final
          this.tabs[3].fields.img_path = this.finalImagePath || this.tabs[3].fields.img_path;
          this.stopLoading();
          return true;
        } else {
          console.error("Error al subir la imagen:", response.data.error);
          this.stopLoading();
          this.$emit("update-show-modal", this.$t('competitions.messages.upload_failed'));
          this.croppedImage = null;
          return false;
        }
      } catch (error) {
        console.error("Error en la subida de la imagen:", error);
        this.stopLoading();
        this.$emit("update-show-modal", this.$t('competitions.messages.upload_failed'));
        this.croppedImage = null;
      }
    },
    dataURLtoFile(dataUrl, filename) {
      let arr = dataUrl.split(",");
      let mime = arr[0].match(/:(.*?);/)[1];
      let bstr = atob(arr[1]);
      let n = bstr.length;
      let u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },
  }
}
</script>

<style scoped>
.height_box{
  overflow-y: auto !important;
  overflow-x: hidden !important;
}
.nav-tabs .nav-link {
  cursor: pointer;
}
.tab-pane.active {
  display: block !important;
}
/*Tab 2*/
.btn-out {
  display: block;
  width: 100%;
  padding: 1rem;
  text-align: center;
  border: 2px solid #bbb;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}
.text-block-border {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 1rem;
}
.btn-out.active,
.bg-same-as-active {
  background-color: #f0f0f0;
  color: #000; /* si hace falta */
  border-color: #777;

}
/*Tab 3*/
.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}
.selected-item {
  background: #007bff;
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  display: flex;
  align-items: center;
}
.remove-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 14px;
  margin-left: 8px;
  cursor: pointer;
}
.remove-btn:hover {
  color: #ff0000;
}
.info-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px; /* Espaciado entre elementos */
  max-width: 100%;
}
.info-box {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  width: calc(33.33% - 20px); /* 3 columnas en escritorio */
  min-width: 120px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f8f9fa;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
}
.info-box i {
  position: absolute;
  top: -15px;
  right: -10px;
  font-size: 17px;
  color: #007bff;
  background: white;
  padding: 5px;
  border-radius: 50%;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
}
.info-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
}
.info-values-grid {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: 10px;
}
.info-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;
}
.info-label {
  font-size: 14px;
  color: #555;
}
.info-value {
  font-size: 18px;
  font-weight: bold;
}
/*Tab 4*/
.no-spin::-webkit-inner-spin-button,
.no-spin::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-spin {
  -moz-appearance: textfield; /* Para Firefox */
  text-align: center;
  padding: 5px;
}
.compact-col {
  padding-left: 5px !important;
  padding-right: 5px !important;
}
.compact-label {
  font-size: 12px; /* Texto más pequeño */
}
.compact-text {
  font-size: 12px; /* Tamaño normal */
}
.image-hover {
  transition: opacity 0.3s ease;
}
.image-hover:hover {
  opacity: 0.7;
  cursor: pointer;
}
</style>